import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowUpRight } from "lucide-react";

const PLANS = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    pitch: "For individual professionals.",
    features: [
      "Professional Passport",
      "Global EthicCred ID",
      "Credibility Score",
      "Unlimited verified records",
      "QR sharing & public verify URL",
    ],
    cta: "Claim free",
    href: "/register",
    accent: false,
  },
  {
    name: "Professional+",
    price: "$9",
    period: "/month",
    pitch: "Stand out with premium credibility.",
    features: [
      "Custom Passport URL",
      "Portfolio Showcase",
      "Resume Builder",
      "Verification Analytics",
      "Priority Verification",
    ],
    cta: "Upgrade",
    href: "/register",
    accent: true,
  },
  {
    name: "Employer",
    price: "$199",
    period: "/month",
    pitch: "For HR teams hiring at scale.",
    features: [
      "Employee Passport Management",
      "Issue Certificates & Recommendations",
      "Issue Conduct Records",
      "Employee Verification",
      "HR Dashboard",
      "Audit Logs",
    ],
    cta: "Talk to sales",
    href: "/register",
    accent: false,
  },
  {
    name: "Institution",
    price: "$299",
    period: "/month",
    pitch: "For universities & schools.",
    features: [
      "Student Passport Management",
      "Degree Verification",
      "Conduct Certificates",
      "Placement Tracking",
      "Alumni Passports",
      "Bulk Issuance",
    ],
    cta: "Get started",
    href: "/register",
    accent: false,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    pitch: "Global workforce credibility.",
    features: [
      "SSO + SCIM",
      "Dedicated success manager",
      "SLA + custom compliance",
      "Private deployment available",
      "Webhooks & full API",
    ],
    cta: "Contact us",
    href: "mailto:sales@ethiccred.com",
    accent: false,
  },
  {
    name: "API",
    price: "Pay-as-you-go",
    period: "",
    pitch: "For developers & platforms.",
    features: [
      "REST + Webhook APIs",
      "Per-verification pricing",
      "Usage dashboard",
      "Rate-limit controls",
      "Sandbox environment",
    ],
    cta: "Read docs",
    href: "/register",
    accent: false,
  },
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-ec-bg" data-testid="pricing-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Pricing</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-3">
            Free for professionals.
            <br />Powerful for everyone else.
          </h1>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            EthicCred is free forever for individual professionals. Employers, institutions and
            developers get advanced verification, analytics and APIs starting at $199/mo.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ec-line border border-ec-line mt-14">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`relative p-8 ${p.accent ? "bg-ec-ink text-white" : "bg-white"} flex flex-col`}
              data-testid={`pricing-${p.name.toLowerCase()}`}
            >
              {p.accent && (
                <span className="absolute top-4 right-4 inline-flex text-[10px] font-bold uppercase tracking-wider bg-ec-emerald text-white px-2 py-0.5">
                  Popular
                </span>
              )}
              <div className="eyebrow" style={p.accent ? { color: "#9ca3af" } : {}}>{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black tracking-tighter">{p.price}</span>
                {p.period && <span className={`text-sm ${p.accent ? "text-zinc-400" : "text-ec-ink-soft"}`}>{p.period}</span>}
              </div>
              <p className={`text-sm mt-2 ${p.accent ? "text-zinc-400" : "text-ec-ink-soft"}`}>{p.pitch}</p>
              <ul className="mt-6 space-y-2 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 ${p.accent ? "text-ec-emerald" : "text-ec-emerald"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to={p.href} className="mt-8" data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                <Button className={`w-full rounded-sm gap-2 ${p.accent ? "bg-white text-ec-ink hover:bg-zinc-200" : "bg-ec-ink text-white hover:bg-zinc-800"}`}>
                  {p.cta} <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          ))}
        </div>

        <ComparisonTable />

        <p className="text-center text-xs eyebrow mt-10 text-ec-ink-soft">
          Stripe & Razorpay billing wired in admin · API keys not yet connected
        </p>
      </div>
      <Footer />
    </div>
  );
}

const COMPARISON = [
  {
    section: "Professional features",
    rows: [
      ["Custom Passport URL", false, true, true, true],
      ["Portfolio Showcase", false, true, true, true],
      ["Resume Builder", false, true, true, true],
      ["Verification Analytics", false, true, true, true],
      ["Priority Verification", false, true, true, true],
    ],
  },
  {
    section: "Employer features",
    rows: [
      ["Employee Passport Management", false, false, true, false],
      ["Issue Certificates", false, false, true, true],
      ["Issue Recommendations", false, false, true, true],
      ["Issue Conduct Records", false, false, true, true],
      ["Employee Verification", false, false, true, true],
      ["HR Dashboard", false, false, true, true],
      ["Audit Logs", false, false, true, true],
    ],
  },
  {
    section: "Institution features",
    rows: [
      ["Student Passport Management", false, false, false, true],
      ["Degree Verification", false, false, false, true],
      ["Conduct Certificates", false, false, true, true],
      ["Placement Tracking", false, false, false, true],
      ["Alumni Passports", false, false, false, true],
      ["Bulk Issuance", false, false, true, true],
    ],
  },
];

const COLUMNS = ["Free", "Professional+", "Employer", "Institution"];

function ComparisonTable() {
  return (
    <div className="mt-20 border border-ec-line bg-white overflow-x-auto" data-testid="pricing-comparison-table">
      <div className="border-b border-ec-line p-6">
        <div className="eyebrow">Compare plans</div>
        <h3 className="font-display text-2xl font-black tracking-tight mt-2">What's in each plan</h3>
      </div>
      <table className="w-full text-sm min-w-[700px]">
        <thead className="bg-ec-mute">
          <tr>
            <th className="text-left px-6 py-3 font-mono-plex text-[11px] uppercase tracking-wider text-ec-ink-soft">Feature</th>
            {COLUMNS.map((c) => (
              <th key={c} className="px-6 py-3 font-display font-bold text-sm">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON.map((sec) => (
            <SectionRows key={sec.section} sec={sec} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionRows({ sec }) {
  return (
    <>
      <tr>
        <td colSpan={5} className="bg-ec-soft px-6 py-3 eyebrow border-y border-ec-line">
          {sec.section}
        </td>
      </tr>
      {sec.rows.map(([label, ...cells]) => (
        <tr key={label} className="border-b border-ec-line">
          <td className="px-6 py-3">{label}</td>
          {cells.map((on, i) => (
            <td key={i} className="text-center px-6 py-3">
              {on ? (
                <CheckCircle2 className="w-4 h-4 text-ec-emerald inline" />
              ) : (
                <span className="text-ec-ink-soft text-lg">—</span>
              )}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
